import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import T from "../theme/colors";

/**
 * Select2 — searchable dropdown with keyboard navigation.
 * - Type to filter options
 * - Arrow Up/Down to navigate, Enter to select, Escape to close
 * - Click to select
 * - Portal-based dropdown (no overflow clip)
 *
 * Phase 36da.99.at — Token-AND search.  The filter now splits the search
 * string on whitespace and requires EVERY token to appear as a substring
 * of the option label, in any order.  Before, the filter did a contiguous
 * substring match — typing "navy mafatlal" would miss a label like
 * "Mafatlal Premium · Sh 12 · Navy Blue" because the words appear in a
 * different order.  With token-AND the same query matches.  Strictly more
 * permissive than the old behaviour, so no existing call site can break.
 */
export default function Select2({
  label, value, onChange, options = [],
  required, style = {}, disabled = false, placeholder,
  hint, error, tabIndex,
  // Phase 37 — optional: notify parent of typed text for SERVER-side search.
  // Omitted → behaves exactly as before (pure client-side filtering).
  onSearch,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [hlIdx, setHlIdx] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));
  // Phase 36da.99.at — memoize so re-renders that don't change search/options
  // (e.g. dropdown position recomputes) don't redo the filter.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    // Split on any whitespace; drop empties from consecutive spaces.
    const tokens = q.split(/\s+/).filter(Boolean);
    return options.filter(o => {
      const lbl = (o.label || "").toLowerCase();
      // Every token must appear somewhere in the label — order-independent.
      return tokens.every(tok => lbl.includes(tok));
    });
  }, [search, options]);

  // Position the portal dropdown
  const updatePos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const dropUp = spaceBelow < 220 && r.top > 220;
    setPos({
      top: dropUp ? r.top - 4 : r.bottom + 4,
      left: r.left,
      width: r.width,
      dropUp,
    });
  }, []);

  const openDrop = () => {
    if (disabled) return;
    if (open) { closeDrop(); return; }
    updatePos();
    setOpen(true);
    setSearch("");
    setHlIdx(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closeDrop = () => {
    setOpen(false);
    setSearch("");
    setHlIdx(-1);
  };

  const pick = (val) => {
    onChange(val);
    closeDrop();
    // Phase 36da.99.tab — return focus to the trigger after selecting (mouse
    // or Enter), so the very next Tab advances to the next field in modal
    // order rather than starting from a lost/blurred focus position.
    setTimeout(() => triggerRef.current?.focus(), 0);
  };

  // Keyboard
  const onKey = (e) => {
    // Phase 36da.99.tab — TAB must keep the natural field order.  The search
    // box lives in a PORTAL (appended to <body>), so once the dropdown is
    // open, a raw Tab would jump focus to whatever is next in body DOM order
    // — not the next field in the modal.  Fix: on Tab while open, commit the
    // highlighted option (if any), close the dropdown, and synchronously
    // return focus to the trigger.  We do NOT preventDefault, so the browser's
    // native Tab then advances from the trigger to the next/prev modal field
    // in correct sequence (Shift+Tab works the same way).
    if (e.key === "Tab") {
      if (open) {
        if (hlIdx >= 0 && hlIdx < filtered.length) {
          onChange(filtered[hlIdx].value);
        }
        setOpen(false);
        setSearch("");
        setHlIdx(-1);
        // Restore focus to the trigger BEFORE the browser processes Tab, so
        // the focus advance starts from the right place in the DOM.
        triggerRef.current?.focus();
      }
      return; // never preventDefault — let native Tab move to the next field
    }
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openDrop();
      }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); closeDrop(); triggerRef.current?.focus(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHlIdx(i => {
        const next = Math.min(i + 1, filtered.length - 1);
        scrollTo(next);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHlIdx(i => {
        const next = Math.max(i - 1, 0);
        scrollTo(next);
        return next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hlIdx >= 0 && hlIdx < filtered.length) pick(filtered[hlIdx].value);
    }
  };

  const scrollTo = (idx) => {
    if (!listRef.current) return;
    const items = listRef.current.children;
    if (items[idx]) items[idx].scrollIntoView({ block: "nearest" });
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const portal = document.getElementById("select2-portal");
        if (portal && portal.contains(e.target)) return;
        closeDrop();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, ...style }} ref={wrapRef}>
      {label && (
        <label style={{ fontSize: 14, fontWeight: 500, color: "var(--text-1)", letterSpacing: "0.02em" }}>
          {label}
          {required && <span style={{ color: "var(--primary)", marginLeft: 2 }}>*</span>}
        </label>
      )}
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={openDrop}
        onKeyDown={onKey}
        tabIndex={disabled ? -1 : (tabIndex != null ? tabIndex : 0)}
        style={{
          position: "relative",
          background: disabled ? "var(--bg-app)" : "var(--bg-input)",
          border: `1px solid ${open ? "var(--border-focus)" : error ? "var(--danger)" : "var(--border)"}`,
          borderRadius: 7,
          boxShadow: open ? `0 0 0 3px var(--primary-dim)` : error ? `0 0 0 3px var(--danger-bg)` : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
          padding: "8px 32px 8px 12px",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", minHeight: 36,
        }}
      >
        <span style={{ fontSize: 15, color: selected ? "var(--text-0)" : "var(--text-2)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : (placeholder || "Select…")}
        </span>
        {value && !required && !disabled && (
          <span
            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onChange(""); }}
            style={{ position: "absolute", right: 28, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "var(--text-2)", display: "flex" }}
          >
            <X size={12} />
          </span>
        )}
        <ChevronDown size={14} style={{ position: "absolute", right: 10, top: "50%", transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, color: "var(--text-2)", pointerEvents: "none", transition: "transform 0.15s" }} />
      </div>

      {error && <span style={{ fontSize: 13, color: "var(--danger)", display: "flex", alignItems: "center", gap: 4 }}>⚠ {error}</span>}
      {hint && !error && <span style={{ fontSize: 13, color: "var(--text-2)" }}>{hint}</span>}

      {/* Portal dropdown */}
      {open && createPortal(
        <div
          id="select2-portal"
          style={{
            position: "fixed",
            top: pos.dropUp ? undefined : pos.top,
            bottom: pos.dropUp ? (window.innerHeight - pos.top) : undefined,
            left: pos.left,
            width: pos.width,
            zIndex: 99999,
            background: "var(--bg-card)",
            border: `1px solid var(--border)`,
            borderRadius: 8,
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            display: "flex", flexDirection: "column",
            maxHeight: 260,
          }}
        >
          {/* Search input */}
          <div style={{ padding: "8px 10px", borderBottom: `1px solid var(--border)`, flexShrink: 0 }}>
            <input
              ref={inputRef}
              value={search}
              onChange={e => { setSearch(e.target.value); setHlIdx(0); if (onSearch) onSearch(e.target.value); }}
              onKeyDown={onKey}
              placeholder="Search…"
              style={{
                width: "100%", padding: "6px 10px", fontSize: 15,
                border: `1px solid var(--border)`, borderRadius: 6,
                background: "var(--bg-input)", color: "var(--text-0)", outline: "none",
              }}
            />
          </div>
          {/* Options list */}
          <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
            {filtered.length === 0 && (
              <div style={{ padding: "12px 14px", textAlign: "center", color: "var(--text-2)", fontSize: 14 }}>No options found</div>
            )}
            {filtered.map((o, i) => {
              const isSelected = String(o.value) === String(value);
              const isHl = i === hlIdx;
              return (
                <div
                  key={o.value}
                  onMouseDown={e => { e.preventDefault(); pick(o.value); }}
                  onMouseEnter={() => setHlIdx(i)}
                  style={{
                    padding: "7px 14px",
                    fontSize: 15,
                    cursor: "pointer",
                    background: isHl ? "var(--primary-dim)" : isSelected ? "var(--bg-hover)" : "transparent",
                    color: isSelected ? "var(--primary)" : "var(--text-0)",
                    fontWeight: isSelected ? 600 : 400,
                    transition: "background 0.1s",
                  }}
                >
                  {o.label}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}